
import { Component, Ref, Watch } from 'vue-property-decorator'
import moment from 'moment'

import Widget from '@/components/Widget/Widget.vue'
import ViewModel from '@/models/ViewModel'
import SearchInput from '@/components/SearchInput/SearchInput.vue'
import { getModule } from 'vuex-module-decorators'
import SystemtModule from '@/store/SystemModule'
import WebMessage from '@/models/WebMessage'
import DataTable from '@/components/DataTable/index.vue'

import PublisherReconciliation, {
  PublisherReconciliationBillingMonth,
} from '@/models/PublisherReconciliation'
import util from '@/mixins/util'
import DropArea from '@/components/DropArea/DropArea.vue'
import publisherReconciliationHomeFields from './publisher-reconciliations-home-fields'

interface ApprovalProgressRow {
  approved_rows: number
  total_rows: number
}

@Component({
  mixins: [util],

  components: {
    Widget,
    SearchInput,
    DataTable,
    DropArea,
  },
})
class PublisherReconciliationHome extends ViewModel {
  @Ref() readonly dataTable!: HTMLFormElement

  public reload_drop_down_area: boolean = false

  public ready: boolean = false

  public records: number = 0

  public loading: boolean = false

  public show_filter_helper: boolean = false

  public filter_mode: string = 'exclusive'

  public query: string[] = []

  public fieldFilters: any = {}

  public fields: Array<any> = []

  public sortBy: string = 'billing_month'

  public sortDesc: boolean = true

  @Watch('filter_mode')
  public onFilterModeChange(): void {
    this.refresh()
  }

  public get show_fields(): Array<any> {
    return this.fields.filter((f: any) => f.show)
  }

  public get query_settings(): Array<any> {
    return []
  }

  public fieldFiltersKeys!: (fieldFilters: any) => string[]

  public async loadBillingMonths(ctx: any): Promise<PublisherReconciliationBillingMonth[]> {
    this.loading = true
    try {
      const field_filters = this.fieldFiltersKeys(this.fieldFilters)

      const response = await PublisherReconciliation.paginate({
        page_size: ctx.perPage,
        page: ctx.currentPage,
        order_by: ctx.sortBy ? ctx.sortBy : 'billing_month',
        order: ctx.sortDesc ? 'desc' : 'asc',
        query: [...ctx.filter, ...field_filters],
        mode: 'exclusive',
      })

      this.records = response.records
      return response.data
    } catch (error: any) {
      WebMessage.error('Failed to load billing months')
      // @no-console
      console.error('Error loading billing months:', error)
      return []
    } finally {
      this.loading = false
    }
  }

  public formatBillingMonth(value: string, forUrl: boolean = false): string {
    if (!value) {
      return ''
    }

    if (forUrl) {
      return moment(value).format('YYYY-MM')
    }

    return moment(value).format('MMMM YYYY')
  }

  public formatStatus(value: string): string {
    if (!value) {
      return ''
    }

    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  public getStatusVariant(status: string): string {
    if (this.isCompletedStatus(status)) {
      return 'success'
    }

    return 'warning'
  }

  public isCompletedStatus(status: string): boolean {
    return status === 'completed' || status === 'approved'
  }

  public getApprovalPercent(row: ApprovalProgressRow): number {
    if (!row.total_rows) {
      return 0
    }

    const percentage: number = Math.round((row.approved_rows * 100) / row.total_rows)
    if (percentage < 0) {
      return 0
    }
    if (percentage > 100) {
      return 100
    }
    return percentage
  }

  public getApprovalVariant(row: ApprovalProgressRow): string {
    const percentage: number = this.getApprovalPercent(row)
    if (percentage >= 100) {
      return 'success'
    }
    if (percentage >= 50) {
      return 'warning'
    }
    return 'secondary'
  }

  public refresh(): void {
    this.dataTable?.refresh()
  }

  public resetFilters(): void {
    this.query = []
    this.clearFilters()
    this.refresh()
  }

  public syncFilters(): void {
    const system = getModule(SystemtModule)
    system.updateState({
      name: 'filters',
      type: 'publisher_reconciliation',
      data: { query: this.query, mode: this.filter_mode },
    })
  }

  public loadFilters(): void {
    const system = getModule(SystemtModule)
    system.getFilter('publisher_reconciliation').then((filter: any) => {
      if (filter) {
        this.query = filter.query
        this.filter_mode = filter.mode
      }
      this.ready = true
      this.refresh()
    })
  }

  public clearFilters(): void {
    const system = getModule(SystemtModule)
    system.updateState({
      name: 'filters',
      type: 'publisher_reconciliation',
      data: null,
    })
  }

  public viewMonth(billingMonth: string): void {
    this.$router.push({
      name: 'publisher-reconciliations-view',
      params: { month: billingMonth },
    })
  }

  public invoice_file: File | null = null

  public uploading_invoice: boolean = false

  public mounted(): void {
    this.fields = publisherReconciliationHomeFields
    this.loadFilters()
  }

  public approveAll(billingMonth: string): void {}

  public rejectAll(billingMonth: string): void {}

  public onInvoiceFileSelected(files: FileList | null): void {
    this.invoice_file = files ? files[0] : null

    this.uploadInvoice()
  }

  public async uploadInvoice(): Promise<void> {
    if (!this.invoice_file) {
      WebMessage.error('Please select a file first')
      return
    }

    this.uploading_invoice = true
    try {
      await PublisherReconciliation.uploadInvoice(this.invoice_file)

      WebMessage.success('Invoice uploaded successfully')
      this.invoice_file = null
      this.refresh()
    } catch (error: any) {
      WebMessage.error('Failed to upload invoice')
      // @no-console
      console.error('Error uploading invoice:', error)
    } finally {
      this.uploading_invoice = false
    }
  }

  public async approveMonth(billingMonth: string): Promise<void> {
    try {
      await PublisherReconciliation.approveMonth(billingMonth)

      WebMessage.success('Month approved successfully')

      this.refresh()
    } catch (error: any) {
      WebMessage.error('Failed to approve month')
      // @no-console
      console.error('Error approving month:', error)
    }
  }

  public async rematchMonth(billingMonth: string): Promise<void> {
    try {
      await PublisherReconciliation.rematch(billingMonth)

      WebMessage.success('Rematch Queued successfully')

      this.refresh()
    } catch (error: any) {
      WebMessage.error('Failed to initiate rematch')
      // @no-console
      console.error('Error initiating rematch:', error)
    }
  }
}

export default PublisherReconciliationHome
